// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Chinese and English, with the choice persisted (design doc §14).
 *
 * **English is the default.** A first visit gets English regardless of what the browser
 * prefers, and the switch on the system page is what changes it. Sniffing
 * `navigator.language` was the obvious thing and the wrong one: the browser's language is
 * a fact about the machine, not about the person in front of a shared scanning station,
 * so on a Chinese-locale Windows box every operator got Chinese whether or not they read
 * it. One stated default plus one visible switch is easier to reason about than a guess
 * that changes per machine.
 *
 * Only labels are translated. Identifiers — archive numbers, device numbers, serials,
 * material codes — are never localized, and neither is data the user entered.
 *
 * The chosen locale is also pushed into the API client, so server-side error messages come
 * back in the same language as the surrounding page rather than in whatever the browser
 * happens to prefer.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { setApiLocale } from "@/api/client";
import { enUS } from "./en-US";
import { zhCN } from "./zh-CN";

export const LOCALE_STORAGE_KEY = "cybertron.locale";
export const SUPPORTED_LOCALES = ["zh-CN", "en-US"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en-US";

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // Private browsing, or storage disabled. The default is fine; the switch still works
    // for this session, it just will not be remembered.
  }
  return DEFAULT_LOCALE;
}

export function changeLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
  setApiLocale(locale);
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Not worth failing a language switch over.
  }
}

const locale = initialLocale();

void i18n.use(initReactI18next).init({
  resources: { "zh-CN": { translation: zhCN }, "en-US": { translation: enUS } },
  lng: locale,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
});

setApiLocale(locale);
document.documentElement.lang = locale;

export default i18n;
